import { ApolloError } from 'apollo-client'
import { Moment } from 'moment-timezone'
import * as React from 'react'

import Divider from 'main/components/common/Divider'
import { IDesiredWeeklyScheduleParams } from 'main/components/family/DesiredWeeklyScheduleFields'
import { IChild } from 'main/gql/child'
import { spacing } from 'main/ui'
import { momentFromDateAndTime } from 'main/util/moment'
import { Mutation, MutationFunction, MutationResult } from 'react-apollo'
import segment from 'util/analytics/segment'
import {
  queryVariables,
  RESCHEDULE_MUTATION,
  RescheduleTour,
  RescheduleTourVariables,
  SCHEDULE_MUTATION,
  SCHEDULE_TOUR_MODAL_QUERY,
  ScheduleTour,
  ScheduleTour_scheduleTour,
  ScheduleTourModal_school,
  ScheduleTourModal_tourOpenings,
  ScheduleTourModal_user,
  ScheduleTourVariables,
} from '../../common/gql'
import ConfirmForm, { IParams as IConfirmFormParams } from './ConfirmForm'
import DetailsReview from './DetailsReview'
import OpeningSelectionReview from './OpeningSelectionReview'

type ScheduleMutationFn = MutationFunction<ScheduleTour, ScheduleTourVariables>
type RescheduleMutationFn = MutationFunction<RescheduleTour, RescheduleTourVariables>

interface IProps {
  anchorMoment: Moment
  desiredWeeklyScheduleParams: IDesiredWeeklyScheduleParams
  school: ScheduleTourModal_school
  selectedChildren: IChild[]
  selectedOpening: ScheduleTourModal_tourOpenings
  tour: ScheduleTour_scheduleTour | null
  user: ScheduleTourModal_user
  onComplete: (tour: ScheduleTour_scheduleTour) => void
  onRequestDetails: () => void
  onRequestOpeningSelection: () => void
}

export default function Review(props: IProps) {
  const {
    desiredWeeklyScheduleParams,
    school,
    selectedChildren,
    selectedOpening,
    user,
    onComplete,
    onRequestDetails,
    onRequestOpeningSelection,
  } = props

  const [error, setError] = React.useState<string | null>(null)

  return (
    <div>
      <OpeningSelectionReview
        school={school}
        selectedOpening={selectedOpening}
        onRequestChange={onRequestOpeningSelection}
      />

      <Divider margin={spacing.majorScale(2)} />

      <DetailsReview
        desiredWeeklyScheduleParams={desiredWeeklyScheduleParams}
        selectedChildren={selectedChildren}
        onRequestChange={onRequestDetails}
      />

      <Divider margin={spacing.majorScale(2)} />

      <Mutation
        mutation={SCHEDULE_MUTATION}
        onCompleted={({ scheduleTour }: { scheduleTour: ScheduleTour_scheduleTour | null }) =>
          handleCompleted(scheduleTour, onComplete)
        }
        onError={(apolloError: ApolloError) => handleError(apolloError, setError)}
        refetchQueries={refetchQueries(props)}
      >
        {(scheduleMutate: ScheduleMutationFn, { loading: scheduleLoading }: MutationResult) => (
          <Mutation
            mutation={RESCHEDULE_MUTATION}
            onCompleted={({
              rescheduleTour,
            }: {
              rescheduleTour: ScheduleTour_scheduleTour | null
            }) => handleCompleted(rescheduleTour, onComplete)}
            onError={(apolloError: ApolloError) => handleError(apolloError, setError)}
            refetchQueries={refetchQueries(props)}
          >
            {(
              rescheduleMutate: RescheduleMutationFn,
              { loading: rescheduleLoading }: MutationResult,
            ) => (
              <ConfirmForm
                error={error}
                loading={scheduleLoading || rescheduleLoading}
                user={user}
                onRequestReset={() => setError(null)}
                onRequestSubmit={params =>
                  handleSubmit(props, params, scheduleMutate, rescheduleMutate)
                }
              />
            )}
          </Mutation>
        )}
      </Mutation>
    </div>
  )
}

function handleSubmit(
  props: IProps,
  confirmParams: IConfirmFormParams,
  scheduleMutate: ScheduleMutationFn,
  rescheduleMutate: RescheduleMutationFn,
) {
  const { desiredWeeklyScheduleParams, school, selectedChildren, selectedOpening, tour, user } =
    props

  const desiredWeeklyScheduleVariables = {
    dropoffAt: desiredWeeklyScheduleParams.dropoffAt as string,
    friday: desiredWeeklyScheduleParams.days.friday,
    monday: desiredWeeklyScheduleParams.days.monday,
    pickupAt: desiredWeeklyScheduleParams.pickupAt as string,
    thursday: desiredWeeklyScheduleParams.days.thursday,
    tuesday: desiredWeeklyScheduleParams.days.tuesday,
    wednesday: desiredWeeklyScheduleParams.days.wednesday,
  }

  const tourVariables = {
    beginsAt: momentFromDateAndTime(
      selectedOpening.date,
      selectedOpening.beginsAt,
      school.timezone,
    ).format(),
    childIds: selectedChildren.map(child => child.id),
    endsAt: momentFromDateAndTime(
      selectedOpening.date,
      selectedOpening.endsAt,
      school.timezone,
    ).format(),
  }

  const userVariables = {
    password: user.hasPassword ? undefined : confirmParams.password,
    phone: confirmParams.phone,
  }

  if (tour) {
    rescheduleMutate({
      variables: {
        desiredWeeklySchedule: desiredWeeklyScheduleVariables,
        tour: tourVariables,
        tourId: tour.id.toString(),
        user: userVariables,
      },
    })
  } else {
    scheduleMutate({
      variables: {
        desiredWeeklySchedule: desiredWeeklyScheduleVariables,
        schoolId: school.id.toString(),
        tour: tourVariables,
        user: userVariables,
      },
    })
  }
}

function handleCompleted(
  tour: ScheduleTour_scheduleTour | null,
  onComplete: (tour: ScheduleTour_scheduleTour) => void,
) {
  if (!tour) throw new Error('no tour')

  segment.track('Parent Schedule Tour', { schoolId: tour.schoolId })
  onComplete(tour)
}

function handleError(error: ApolloError, setError: (error: string) => void) {
  const messages = error.graphQLErrors.map(e => e.message)

  if (messages.includes('begins_at is not available')) {
    setError('This time was already booked! Please select another time.')
  } else {
    setError('Something went wrong! Please try again.')
  }
}

function refetchQueries(props: IProps) {
  const { anchorMoment, school } = props

  return [{ query: SCHEDULE_TOUR_MODAL_QUERY, variables: queryVariables(school.id, anchorMoment) }]
}
